import { useState } from 'react'
import { supabase } from '../lib/supabase'

export type TeamMember = {
  id: string;
  name: string;
  role: string;
  description: string;
  image: string;
  email?: string;
  linkedin?: string;
  website?: string;
  created_at?: string;
  updated_at?: string;
}

function useTeamMembers() {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchMembers = async () => {
    setLoading(true);
    try {
      console.log('Fetching team members...');
      const { data, error } = await supabase
        .from('team_members')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching members:', error);
        throw error;
      }

      console.log('Team members fetched:', data);
      const processedData = data?.map(member => {
        let imageUrl = member.image;
        if (imageUrl && !imageUrl.startsWith('http')) {
          const { data: { publicUrl } } = supabase.storage
            .from('team')
            .getPublicUrl(imageUrl);
          imageUrl = publicUrl;
        }
        return {
          ...member,
          image: imageUrl || null
        };
      });

      return processedData as TeamMember[];
    } catch (err) {
      console.error('Error fetching team members:', err)
      throw err
    } finally {
      setLoading(false);
    }
  }

  const createMember = async (member: Omit<TeamMember, 'id' | 'created_at' | 'updated_at'>) => {
    setLoading(true)
    setError(null)
    console.log('Creating member:', member);

    try {
      const newMember = {
        name: member.name,
        role: member.role,
        description: member.description,
        image: member.image || null,
        email: member.email || null,
        linkedin: member.linkedin?.trim() || null,
        website: member.website?.trim() || null,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      console.log('Inserting member into Supabase:', newMember);

      const { data, error } = await supabase
        .from('team_members')
        .insert([newMember])
        .select()
        .single();

      if (error) {
        console.error('Supabase error:', error);
        throw error;
      }

      console.log('Member created successfully:', data);
      await fetchMembers(); // Refresh members list after creation

      return data as TeamMember;
    } catch (err: any) {
      console.error('Error creating member:', err);
      setError(err.message)
      throw err
    } finally {
      setLoading(false)
    }
  }

  const updateMember = async (id: string, updates: Partial<TeamMember>) => {
    setLoading(true)
    setError(null)
    console.log('Updating member:', { id, updates });

    try {
      // First check if the member exists
      const { data: existingMember, error: checkError } = await supabase
        .from('team_members')
        .select('*')
        .eq('id', id)
        .single();

      if (checkError || !existingMember) {
        throw new Error('Member not found');
      }
      console.log('Existing member found:', existingMember);

      // Prepare update data
      const updateData = {
        name: updates.name,
        role: updates.role,
        description: updates.description,
        image: updates.image || null,
        email: updates.email || null,
        linkedin: updates.linkedin || null,
        website: updates.website || null,
        updated_at: new Date().toISOString()
      };

      console.log('Updating member with data:', updateData);

      const { data: updatedMember, error: updateError } = await supabase
        .from('team_members')
        .update(updateData)
        .eq('id', id)
        .select()
        .single();

      if (updateError) throw updateError;
      if (!updatedMember) throw new Error('Failed to update member');

      console.log('Member updated successfully:', updatedMember);
      await fetchMembers(); // Refresh members list after update

      return updatedMember as TeamMember;
    } catch (err: any) {
      console.error('Error updating team member:', err)
      setError(err.message)
      throw err
    } finally {
      setLoading(false)
    }
  }

  const deleteMember = async (id: string) => {
    setLoading(true)
    setError(null)
    console.log('Deleting member:', id);

    try {
      const { error } = await supabase
        .from('team_members')
        .delete()
        .eq('id', id)

      if (error) {
        console.error('Error deleting from Supabase:', error);
        throw error;
      }

      console.log('Member deleted successfully');
      await fetchMembers(); // Refresh members list after deletion

    } catch (err: any) {
      console.error('Error deleting team member:', err)
      setError(err.message)
      throw err
    } finally {
      setLoading(false)
    }
  }

  return {
    loading,
    error,
    fetchMembers,
    createMember,
    updateMember,
    deleteMember
  }
}

export type { TeamMember };
export default useTeamMembers;